<!-- Dialog title -->
<h1 mat-dialog-title>
  <!-- Title with icon -->
  <div class="flex flex-row items-center">
    <!-- Icon -->
    <div
      class="flex flex-row items-center mx-2 justify-center bg-accent-900 rounded-full h-10 w-10"
    >
      <mat-icon class="icon-size-5" color="primary" svgIcon="mat_solid:calendar_today"></mat-icon>
    </div>
    <!-- Dialog title text -->
    <span>{{ 'Generate Weekly Report' | transloco }}</span>
  </div>
</h1>
<mat-divider></mat-divider>

<!-- Dialog content -->
<div class="flex flex-col p-12" mat-dialog-content>
  <form [formGroup]="reportForm">
    <!-- Report Name Field -->
    <div class="flex flex-row w-full gap-2">
      <!-- Report Name Input -->
      <mat-form-field class="w-full" appearance="fill">
        <mat-label>{{ 'Report Name' | transloco }}</mat-label>
        <input matInput type="text" formControlName="name" (blur)="onNameFieldBlur()" />
        <!-- Character counter hint (shows trimmed length) -->
        <mat-hint align="end">{{ getTrimmedNameLength() }}/255</mat-hint>
        <!-- Error handling for Report Name -->
        <mat-error *ngIf="nameFieldTouched && reportForm.get('name').hasError('required')">
          {{ 'Report name is required' | transloco }}
        </mat-error>
        <mat-error *ngIf="reportForm.get('name').hasError('maxlength')">
          {{ 'Report name cannot exceed 255 characters (excluding spaces)' | transloco }}
        </mat-error>
      </mat-form-field>
    </div>

    <!-- Start Date and End Date Fields -->
    <div class="flex flex-row w-full gap-4">
      <!-- Start Date -->
      <div class="flex flex-row w-1/2 gap-2">
        <!-- Start Date Input -->
        <mat-form-field appearance="fill" class="w-full">
          <mat-label>{{ 'Start Date Weekly Report' | transloco }}</mat-label>
          <input
            (dateChange)="onStartDateChange($event.value)"
            [matDatepicker]="startPicker"
            [placeholder]="'Choose a date' | transloco"
            matInput
            formControlName="start_date"
            readonly
          />
          <mat-datepicker-toggle [for]="startPicker" matSuffix></mat-datepicker-toggle>
          <mat-datepicker #startPicker></mat-datepicker>
          <!-- Error handling for Start Date -->
          <mat-error *ngIf="reportForm.get('start_date').hasError('required')">
            {{ 'Start date is required' | transloco }}
          </mat-error>
        </mat-form-field>
      </div>
      <!-- End Date -->
      <div class="flex flex-row w-1/2 gap-2">
        <!-- End Date Input -->
        <mat-form-field appearance="fill" class="w-full">
          <mat-label>{{ 'End Date Weekly Report' | transloco }}</mat-label>
          <input matInput [value]="endDate" readonly />
          <!-- You can add error handling for End Date if necessary -->
        </mat-form-field>
      </div>
    </div>

    <!-- Planned Progress,and Actual Progress -->
    <div class="flex flex-row w-full gap-4">
      <!-- Planned Progress -->
      <div class="flex flex-row w-1/2 gap-2">
        <!-- Planned Progress Input -->
        <mat-form-field appearance="fill" class="w-full">
          <mat-label>{{ 'Planned Progress' | transloco }} (%)</mat-label>
          <input
            matInput
            [max]="100"
            [min]="0"
            type="number"
            class="no-spinner"
            formControlName="planned_progress_percentage"
          />
          <!-- Error handling for Planned Progress -->
          <mat-error *ngIf="reportForm.get('planned_progress_percentage').hasError('required')">
            {{ 'Planned progress is required' | transloco }}
          </mat-error>
          <mat-error
            *ngIf="
              reportForm.get('planned_progress_percentage').hasError('min') ||
              reportForm.get('planned_progress_percentage').hasError('max')
            "
          >
            {{ 'Planned progress must be between 0 and 100' | transloco }}
          </mat-error>
        </mat-form-field>
      </div>
      <!-- Actual Progress -->
      <div class="flex flex-row w-1/2 gap-2">
        <!-- Actual Progress Input -->
        <mat-form-field appearance="fill" class="w-full">
          <span matSuffix>
            <mat-spinner *ngIf="isLastPhaseLoading" [diameter]="20"></mat-spinner>
          </span>
          <mat-label>{{ 'Actual Progress' | transloco }} (%)</mat-label>
          <mat-icon
            matSuffix
            [matTooltip]="
              'This field is to be read from WH platform in case of added phases and can be entered manually'
                | transloco
            "
            svgIcon="mat_solid:help_outline"
          >
          </mat-icon>
          <input
            matInput
            type="number"
            [max]="100"
            [min]="0"
            [readonly]="isLastPhaseLoading"
            [ngClass]="isLastPhaseLoading ? 'opacity-50' : 'opacity-100'"
            class="no-spinner"
            formControlName="actual_progress_percentage"
          />
          <!-- Error handling for Actual Progress -->
          <mat-error *ngIf="reportForm.get('actual_progress_percentage').hasError('required')">
            {{ 'Actual progress is required' | transloco }}
          </mat-error>
          <mat-error
            *ngIf="
              reportForm.get('actual_progress_percentage').hasError('min') ||
              reportForm.get('actual_progress_percentage').hasError('max')
            "
          >
            {{ 'Actual progress must be between 0 and 100' | transloco }}
          </mat-error>
        </mat-form-field>
      </div>
    </div>

    <!-- Financial Status -->
    <div class="flex flex-row justify-between items-center mb-2">
      <mat-label>{{ 'Financial Status' | transloco }}</mat-label>
      <!-- Financial Input Type Radio Buttons (Only for NWC Clients) -->
      <mat-radio-group
        *ngIf="isNwcClient"
        formControlName="financial_input_type"
        color="primary"
        class="flex flex-row items-center gap-4"
      >
        <mat-radio-button value="sar">
          {{ 'SAR' | transloco }}
        </mat-radio-button>
        <mat-radio-button value="percentage">
          {{ 'Percentage' | transloco }}
        </mat-radio-button>
      </mat-radio-group>
    </div>
    <div class="flex flex-row w-full gap-2">
      <!-- Actual Financial Status Input -->
      <div class="flex flex-row w-1/3 gap-2">
        <mat-form-field class="w-full" appearance="fill">
          <mat-label>{{ 'Actual' | transloco }}</mat-label>
          <input
            matInput
            type="number"
            class="no-spinner"
            formControlName="actual_cost"
            [readonly]="isLastPhaseLoading"
            [ngClass]="isLastPhaseLoading ? 'opacity-50' : 'opacity-100'"
            [max]="financialInputType === 'percentage' ? 100 : null"
            [min]="0"
            (input)="onFinancialInputChange('actual_cost', $event)"
          />
          <span matSuffix class="flex items-center gap-2">
            <mat-spinner *ngIf="isLastPhaseLoading" [diameter]="20"></mat-spinner>
            <span *ngIf="!isLastPhaseLoading && isNwcClient && financialInputType === 'percentage'"
              >%</span
            >
            <span *ngIf="!isLastPhaseLoading && isNwcClient && financialInputType === 'sar'">{{
              'SAR' | transloco
            }}</span>
          </span>
          <!-- Error handling for Actual Financial Status -->
          <mat-error *ngIf="reportForm.get('actual_cost').hasError('required')">
            {{ 'Actual financial status is required' | transloco }}
          </mat-error>
          <mat-error
            *ngIf="
              financialInputType === 'percentage' &&
              (reportForm.get('actual_cost').hasError('min') ||
                reportForm.get('actual_cost').hasError('max'))
            "
          >
            {{ 'Value must be between 0 and 100' | transloco }}
          </mat-error>
        </mat-form-field>
      </div>
      <!-- Remaining Financial Status Input -->
      <div class="flex flex-row w-1/3 gap-2">
        <mat-form-field class="w-full" appearance="fill">
          <mat-label>{{ 'Remaining' | transloco }}</mat-label>
          <input
            matInput
            type="number"
            class="no-spinner"
            formControlName="remaining_cost"
            [max]="financialInputType === 'percentage' ? 100 : null"
            [min]="0"
            (input)="onFinancialInputChange('remaining_cost', $event)"
          />
          <span matSuffix>
            <span *ngIf="isNwcClient && financialInputType === 'percentage'">%</span>
            <span *ngIf="isNwcClient && financialInputType === 'sar'">{{ 'SAR' | transloco }}</span>
          </span>
          <!-- Error handling for Remaining Financial Status -->
          <mat-error *ngIf="reportForm.get('remaining_cost').hasError('required')">
            {{ 'Remaining financial status is required' | transloco }}
          </mat-error>
          <mat-error
            *ngIf="
              financialInputType === 'percentage' &&
              (reportForm.get('remaining_cost').hasError('min') ||
                reportForm.get('remaining_cost').hasError('max'))
            "
          >
            {{ 'Value must be between 0 and 100' | transloco }}
          </mat-error>
        </mat-form-field>
      </div>
      <!-- Pending Financial Status Input -->
      <div class="flex flex-row w-1/3 gap-2">
        <mat-form-field class="w-full" appearance="fill">
          <mat-label
            >{{ 'Pending Invoices' | transloco }}
            <span class="text-secondary">({{ 'Optional' | transloco }})</span></mat-label
          >
          <input
            matInput
            type="number"
            class="no-spinner"
            formControlName="pending_cost"
            [max]="financialInputType === 'percentage' ? 100 : null"
            [min]="0"
            (input)="onFinancialInputChange('pending_cost', $event)"
          />
          <span matSuffix>
            <span *ngIf="isNwcClient && financialInputType === 'percentage'">%</span>
            <span *ngIf="isNwcClient && financialInputType === 'sar'">{{ 'SAR' | transloco }}</span>
          </span>
          <mat-error
            *ngIf="
              financialInputType === 'percentage' &&
              (reportForm.get('pending_cost').hasError('min') ||
                reportForm.get('pending_cost').hasError('max'))
            "
          >
            {{ 'Value must be between 0 and 100' | transloco }}
          </mat-error>
        </mat-form-field>
      </div>
    </div>

    <mat-divider class="my-3"></mat-divider>

    <!-- Deviation Graph Section -->
    <mat-label
      >{{ 'Deviation Graph' | transloco
      }}<span class="ms-1 text-secondary">({{ 'Optional' | transloco }})</span></mat-label
    >

    <!-- Image Upload Section -->
    <div class="flex flex-row w-full gap-2">
      <div class="mx-auto" style="max-width: 300px">
        <!-- Cropped Image Display -->
        <div
          *ngIf="reportDetails?.deviation"
          class="border border-accent-500 rounded-md bg-accent-800 p-1 cursor-pointer relative"
        >
          <img
            (click)="openImageDialog()"
            [src]="reportDetails?.deviation + '?v=' + timestamp"
            alt=""
            class="rounded-md"
          />
          <div
            class="flx-col justify-center items-center absolute-x-center bottom-3"
            id="updateProjectNoImageMessage"
          >
            <div class="light rounded-md font-bold bg-accent-500 p-2" (click)="openImageDialog()">
              <small class="text-primary">{{ 'Click to replace' | transloco }}</small>
            </div>
          </div>
        </div>

        <div
          *ngIf="croppedImage && !reportDetails?.deviation"
          class="border border-accent-500 rounded-md bg-accent-800 p-1"
        >
          <img [src]="croppedImage" alt="" class="rounded-md" id="newProjectProjectLogoImage" />
          <!-- Replace and Remove Buttons -->
          <div class="flx-row justify-end items-center mt-2">
            <button (click)="openImageDialog()" mat-button type="button">
              {{ 'Replace' | transloco }}
            </button>
            <button (click)="removeImg()" mat-button type="button">
              {{ 'Remove' | transloco }}
            </button>
          </div>
        </div>
        <!-- Image Upload Button -->
        <button
          (click)="openImageDialog()"
          *ngIf="!croppedImage && !reportDetails?.deviation"
          class="border border-accent-500 rounded-md bg-accent-800 border-dashed w-full p-1 relative"
          id="newProjectProjectLogoBtnUpload"
          type="button"
        >
          <img alt="" class="rounded-md" src="assets/images/project_placeholder.png" />
          <div class="flx-col justify-center items-center absolute-x-center bottom-3">
            <div class="light bg-transparent text-secondary font-bold">
              {{ 'No Image Yet' | transloco }}<br />
              <small>{{ 'Click to upload' | transloco }}</small>
            </div>
          </div>
        </button>
      </div>
    </div>

    <!-- Dynamic Achievements Section -->
    <div class="flex flex-col w-full gap-4">
      <div class="flex flex-row justify-between items-center">
        <!-- Achievements Title -->
        <h3>
          {{ 'Achievements' | transloco }}
          <span class="ms-1 text-secondary">({{ 'Optional' | transloco }})</span>
        </h3>
        <!-- Add Achievement Button -->
        <button mat-icon-button color="primary" (click)="addAchievement()">
          <mat-icon svgIcon="mat_solid:add"></mat-icon>
        </button>
      </div>

      <!-- Loop through achievements -->
      <div formArrayName="achievements_description">
        <div
          *ngFor="let achievement of achievements.controls; let i = index"
          [formGroupName]="i"
          class="flex flex-col justify-between items-center w-full my-4 bg-accent-900 rounded-md p-4"
        >
          <div class="flex flex-row w-full justify-between">
            <span class="text-lg font-bold">{{
              ('Achievement' | transloco) + ' #' + (i + 1)
            }}</span>
            <!-- Remove Button -->
            <button mat-icon-button color="warn" (click)="removeAchievement(i)">
              <mat-icon [svgIcon]="'mat_solid:delete_outline'"></mat-icon>
            </button>
          </div>
          <div class="flex flex-row w-full">
            <!-- Achievement Description -->
            <mat-form-field class="flex-grow" appearance="fill">
              <mat-label>{{ 'Description' | transloco }}</mat-label>
              <textarea matInput type="text" formControlName="description"> </textarea>
              <!-- Error handling for Achievement Description -->
              <mat-error *ngIf="achievement.get('description').hasError('required')">
                {{ 'Description is required' | transloco }}
              </mat-error>
            </mat-form-field>
          </div>
          <div class="flex flex-row w-full items-center gap-2">
            <!-- Achievement Status -->
            <mat-form-field appearance="fill" class="w-1/2">
              <mat-label>{{ 'Status' | transloco }}</mat-label>
              <input matInput type="text" formControlName="status" />
              <!-- Error handling for Achievement Status -->
              <mat-error *ngIf="achievement.get('status').hasError('required')">
                {{ 'Status is required' | transloco }}
              </mat-error>
            </mat-form-field>
            <!-- Achievement Date -->
            <mat-form-field appearance="fill" class="w-1/2">
              <mat-label>{{ 'Date' | transloco }}</mat-label>
              <input
                [matDatepicker]="achievementDatePicker"
                [placeholder]="'Choose a date' | transloco"
                matInput
                formControlName="date"
                readonly
              />
              <mat-datepicker-toggle
                [for]="achievementDatePicker"
                matSuffix
              ></mat-datepicker-toggle>
              <mat-datepicker #achievementDatePicker></mat-datepicker>
              <!-- Error handling for Achievement Date -->
              <mat-error *ngIf="achievement.get('date').hasError('required')">
                {{ 'Date is required' | transloco }}
              </mat-error>
            </mat-form-field>
          </div>
        </div>
        <mat-divider></mat-divider>
        <div *ngIf="achievements.length" class="flex flex-row w-full justify-center">
          <!-- Add More Achievements Button -->
          <button class="text-primary" (click)="addAchievement()">
            {{ 'Add More Achievements' | transloco }}
          </button>
        </div>
      </div>
    </div>
    <!-- Dynamic Challenges Section -->
    <div class="flex flex-col w-full gap-4">
      <div class="flex flex-row justify-between items-center">
        <!-- Challenges Title -->
        <h3>
          {{ 'Challenges' | transloco
          }}<span class="ms-1 text-secondary">({{ 'Optional' | transloco }})</span>
        </h3>
        <!-- Add Challenges Button -->
        <button mat-icon-button color="primary" (click)="addChallenges()">
          <mat-icon svgIcon="mat_solid:add"></mat-icon>
        </button>
      </div>

      <!-- Loop through challenges -->
      <div formArrayName="challenges_description">
        <div
          *ngFor="let challenge of challenges.controls; let i = index"
          [formGroupName]="i"
          class="flex flex-col justify-between items-center w-full my-4 bg-accent-900 rounded-md p-4"
        >
          <div class="flex flex-row w-full justify-between">
            <span class="text-lg font-bold">{{ ('Challenge' | transloco) + ' #' + (i + 1) }}</span>
            <!-- Remove Button -->
            <button mat-icon-button color="warn" (click)="removeChallenges(i)">
              <mat-icon [svgIcon]="'mat_solid:delete_outline'"></mat-icon>
            </button>
          </div>
          <div class="flex flex-row w-full">
            <!-- Challenge Description -->
            <mat-form-field class="flex-grow" appearance="fill">
              <mat-label>{{ 'Description' | transloco }}</mat-label>
              <textarea matInput type="text" formControlName="description"> </textarea>
              <!-- Error handling for Challenge Description -->
              <mat-error *ngIf="challenge.get('description').hasError('required')">
                {{ 'Description is required' | transloco }}
              </mat-error>
            </mat-form-field>
          </div>
          <div class="flex flex-row w-full items-center gap-2">
            <!-- Challenges Status -->
            <mat-form-field appearance="fill" class="w-1/2">
              <mat-label>{{ 'Status' | transloco }}</mat-label>
              <input matInput type="text" formControlName="status" />
              <!-- Error handling for Achievement Status -->
              <mat-error *ngIf="challenge.get('status').hasError('required')">
                {{ 'Status is required' | transloco }}
              </mat-error>
            </mat-form-field>
            <!-- Challenge Date -->
            <mat-form-field appearance="fill" class="w-1/2">
              <mat-label>{{ 'Closing Date' | transloco }}</mat-label>
              <input
                [matDatepicker]="challengeDatePicker"
                [placeholder]="'Choose a date' | transloco"
                matInput
                formControlName="date"
                readonly
              />
              <mat-datepicker-toggle [for]="challengeDatePicker" matSuffix></mat-datepicker-toggle>
              <mat-datepicker #challengeDatePicker></mat-datepicker>
              <!-- Error handling for Challenge Date -->
              <mat-error *ngIf="challenge.get('date').hasError('required')">
                {{ 'Closing date is required' | transloco }}
              </mat-error>
            </mat-form-field>
          </div>
          <div class="flex flex-row w-full">
            <!-- Challenges Taken Action -->
            <mat-form-field appearance="fill" class="flex-grow">
              <mat-label>{{ 'Taken Action' | transloco }}</mat-label>
              <input matInput type="text" formControlName="actionNeeded" />
              <!-- Error handling for Challenges Status -->
              <mat-error *ngIf="challenge.get('actionNeeded').hasError('required')">
                {{ 'Taken action is required' | transloco }}
              </mat-error>
            </mat-form-field>
          </div>
        </div>
        <mat-divider></mat-divider>
        <div *ngIf="challenges.length" class="flex flex-row w-full justify-center">
          <!-- Add More Challenges Button -->
          <button class="text-primary" (click)="addChallenges()">
            {{ 'Add More Challenges' | transloco }}
          </button>
        </div>
      </div>
    </div>
  </form>
</div>

<!-- Dialog actions -->
<div class="flx-row justify-end" mat-dialog-actions>
  <!-- Cancel button -->
  <button (click)="onNoClick()" mat-button>
    {{ 'Cancel' | transloco }}
  </button>
  <!-- Generate button -->
  <button
    appLoadingButton
    [loadingState]="isGenerating"
    color="primary"
    mat-raised-button
    type="submit"
    [disabled]="reportForm.invalid"
    (click)="data.reportId ? updateReport() : submitReport()"
  >
    {{ data.reportId ? ('Update' | transloco) : ('Generate' | transloco) }}
  </button>
</div>
